import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Button, Form, Row, Col, Badge, Alert as BsAlert } from "react-bootstrap";
import SafetyNomineService from "../../../services/safety/SafetyNomineService";
import SafetySediOperativeService from "../../../services/safety/SafetySediOperativeService";
import LavoratoreSearchPicker from "../LavoratoreSearchPicker/LavoratoreSearchPicker";

const EMPTY = {
    id_tipo_nom: "",
    tipo_soggetto: "LAVORATORE",
    id_lavoratore: "",
    nominativo: "",
    ente_esterno: "",
    titolo: "",
    data_nomina: "",
    data_scadenza: "",
    periodicita_mesi: "",
    note: "",
};

const NOM_HELP = {
    RSPP: "Figura obbligatoria: responsabile del servizio di prevenzione e protezione. Può essere interno o consulente esterno.",
    MEDICO_COMPETENTE: "Figura obbligatoria per la sorveglianza sanitaria e il protocollo sanitario.",
    RLS: "Obbligatorio nelle aziende con almeno 15 lavoratori (art. 47). Registra la nomina e l'aggiornamento formativo.",
    ADD_ANTINCENDIO: "Addetti alla squadra antincendio: registra nomina e scadenza aggiornamento corso (tipicamente 36 mesi).",
    ADD_PRIMO_SOCCORSO: "Addetti al primo soccorso: registra nomina e scadenza aggiornamento corso.",
};

function fmtDate(d) {
    if (!d) return "—";
    const s = String(d).substring(0, 10);
    const p = s.split("-");
    if (p.length !== 3) return s;
    return `${p[2]}/${p[1]}/${p[0]}`;
}

function groupByCategoria(tipi) {
    const groups = {};
    tipi.forEach(t => {
        const cat = t.categoria || "Altri";
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(t);
    });
    return Object.entries(groups);
}

function statoBadge(stato, giorni) {
    const map = {
        SCADUTO:     { bg: "danger",  text: "light", label: "Aggiornamento scaduto" },
        IN_SCADENZA: { bg: "warning", text: "dark",  label: "Aggiornamento in scadenza" },
        VALIDO:      { bg: "success", text: "light", label: "In regola" },
        PERMANENTE:  { bg: "light",   text: "dark",  label: "Senza scadenza" },
    };
    const m = map[stato] || map.PERMANENTE;
    return (
        <span>
            <Badge bg={m.bg} text={m.text} className={m.bg === "light" ? "border" : undefined}>{m.label}</Badge>
            {typeof giorni === "number" && (stato === "SCADUTO" || stato === "IN_SCADENZA") && (
                <span className="text-muted small ms-1">
                    ({giorni < 0 ? `${Math.abs(giorni)} gg fa` : `tra ${giorni} gg`})
                </span>
            )}
        </span>
    );
}

function displayIncaricato(n) {
    if (n.nominativo) return n.nominativo;
    if (n.lav_cognome) return `${n.lav_cognome} ${n.lav_nome || ""}`.trim();
    return "—";
}

export default function NominePanel({ idAzienda }) {
    const [tipi, setTipi] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorTipi, setErrorTipi] = useState(null);
    const [errorItems, setErrorItems] = useState(null);
    const [msg, setMsg] = useState(null);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [sedi, setSedi] = useState([]);
    const [selectedSedi, setSelectedSedi] = useState([]);

    const loadTipi = useCallback(async () => {
        try {
            const resp = await SafetyNomineService.getTipi();
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            if (Array.isArray(data)) {
                setTipi(data);
                setErrorTipi(null);
            } else if (data?.message || data?.error) {
                setTipi([]);
                setErrorTipi(data.message || data.error);
            } else setTipi([]);
        } catch (err) {
            setTipi([]);
            const m = err?.message || err?.error?.message || "";
            setErrorTipi(m.includes("NetworkError") || m.includes("Failed to fetch")
                ? "API non raggiungibile. Deploy backend (SafetyNomineController + Service) e auth.json."
                : (m || "Impossibile caricare il catalogo figure."));
        }
    }, []);

    const loadItems = useCallback(async () => {
        if (!idAzienda) { setItems([]); return; }
        setLoading(true);
        setErrorItems(null);
        try {
            const resp = await SafetyNomineService.byAzienda(idAzienda);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            const m = err?.message || err?.error?.message || "";
            setErrorItems(m || "Errore nel caricamento delle nomine registrate.");
        } finally {
            setLoading(false);
        }
    }, [idAzienda]);

    const loadSedi = useCallback(async () => {
        if (!idAzienda) { setSedi([]); return; }
        try {
            const resp = await SafetySediOperativeService.findByAzienda(idAzienda);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setSedi(Array.isArray(data) ? data : []);
        } catch {
            setSedi([]);
        }
    }, [idAzienda]);

    useEffect(() => { loadTipi(); }, [loadTipi]);
    useEffect(() => { loadItems(); }, [loadItems]);
    useEffect(() => { loadSedi(); }, [loadSedi]);

    const toggleSede = (id) => {
        const n = Number(id);
        setSelectedSedi(prev => prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n]);
    };

    const openForm = () => {
        setForm(EMPTY);
        setFile(null);
        setSelectedSedi([]);
        setErrorItems(null);
        setMsg(null);
        setShow(true);
    };

    const submit = async () => {
        if (!idAzienda) return;
        if (!form.id_tipo_nom && !form.titolo) {
            setErrorItems("Seleziona una figura o indica un titolo.");
            return;
        }
        if (form.tipo_soggetto === "LAVORATORE" && !form.id_lavoratore) {
            setErrorItems("Seleziona il lavoratore incaricato.");
            return;
        }
        if (form.tipo_soggetto === "ESTERNO" && !form.nominativo) {
            setErrorItems("Indica il nominativo per incarico esterno.");
            return;
        }
        setSaving(true);
        setErrorItems(null);
        setMsg(null);
        try {
            const fd = new FormData();
            fd.append("id_azienda", idAzienda);
            if (form.id_tipo_nom)       fd.append("id_tipo_nom", form.id_tipo_nom);
            if (form.titolo)             fd.append("titolo", form.titolo);
            fd.append("tipo_soggetto", form.tipo_soggetto);
            if (form.id_lavoratore)      fd.append("id_lavoratore", form.id_lavoratore);
            if (form.nominativo)         fd.append("nominativo", form.nominativo);
            if (form.ente_esterno)       fd.append("ente_esterno", form.ente_esterno);
            if (form.data_nomina)        fd.append("data_nomina", form.data_nomina);
            if (form.data_scadenza)      fd.append("data_scadenza", form.data_scadenza);
            if (form.periodicita_mesi)   fd.append("periodicita_mesi", form.periodicita_mesi);
            if (form.note)               fd.append("note", form.note);
            if (selectedSedi.length)     fd.append("id_sedi", selectedSedi.join(","));
            if (file)                    fd.append("file", file);
            await SafetyNomineService.create(fd);
            setMsg("Nomina registrata.");
            setShow(false);
            setForm(EMPTY);
            setFile(null);
            setSelectedSedi([]);
            await loadItems();
        } catch (err) {
            setErrorItems(err?.message || "Errore nel salvataggio.");
        } finally {
            setSaving(false);
        }
    };

    const onDownload = async (n) => {
        setErrorItems(null);
        try {
            const fn = `${(n.titolo || n.tipo_denominazione || "nomina").toString().replace(/[^\w\-]+/g, "_")}.pdf`;
            await SafetyNomineService.download(n.id_nomina, fn);
        } catch (err) {
            setErrorItems(err?.message || "Impossibile scaricare il file.");
        }
    };

    const onDelete = async (n) => {
        if (!n?.id_nomina) return;
        if (!window.confirm("Eliminare questa nomina?")) return;
        try {
            await SafetyNomineService.deleteNomina(n.id_nomina);
            await loadItems();
        } catch (err) {
            setErrorItems(err?.message || "Errore nell'eliminazione.");
        }
    };

    const tipoSel = form.id_tipo_nom ? tipi.find(t => Number(t.id_tipo_nom) === Number(form.id_tipo_nom)) : null;
    const tipoHelp = tipoSel ? NOM_HELP[tipoSel.codice] : null;
    const presentTipoIds = new Set(items.filter(i => i.id_tipo_nom).map(i => Number(i.id_tipo_nom)));
    const mancanti = tipi.filter(t => Number(t.obbligatorio) === 1 && !presentTipoIds.has(Number(t.id_tipo_nom)));

    if (!idAzienda) {
        return (
            <BsAlert variant="info" className="py-2 mb-0">
                Salva prima i dati dell'azienda per gestire le nomine e le figure della sicurezza.
            </BsAlert>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                    <i className="bi bi-person-badge me-2 text-primary" />
                    Nomine e figure della sicurezza
                </h6>
                <Button size="sm" variant="primary" onClick={openForm} disabled={show}>
                    <i className="bi bi-plus-lg me-1" />
                    Aggiungi nomina
                </Button>
            </div>

            <div className="text-muted small mb-2">
                Registra <strong>chi</strong> ricopre le figure 81/08 (RSPP, medico competente, RLS, preposti, addetti emergenze).
                Il decreto/verbale di nomina può essere archiviato qui; nel tab Documenti restano le copie di fascicolo generale.
            </div>

            {mancanti.length > 0 && (
                <BsAlert variant="warning" className="py-2 small">
                    <strong>Figure obbligatorie da registrare ({mancanti.length}):</strong>{" "}
                    {mancanti.map(m => m.denominazione).join(", ")}.
                    {" "}Usa <strong>Aggiungi nomina</strong> per indicare chi ricopre ciascun ruolo.
                </BsAlert>
            )}

            {errorTipi && <BsAlert variant="danger" className="py-2 small">{errorTipi}</BsAlert>}
            {errorItems && <BsAlert variant="danger" className="py-2 small">{errorItems}</BsAlert>}
            {msg && <BsAlert variant="success" className="py-2 small" onClose={() => setMsg(null)} dismissible>{msg}</BsAlert>}

            {show && (
                <div className="border rounded p-3 mb-3" style={{ backgroundColor: "#f8f9fa" }}>
                    <div className="fw-semibold mb-2">Nuova nomina / figura</div>
                    <Row className="g-2">
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Figura</Form.Label>
                            <Form.Select size="sm" value={form.id_tipo_nom} onChange={e => setForm(f => ({ ...f, id_tipo_nom: e.target.value }))}>
                                <option value="">— Altro / personalizzato —</option>
                                {groupByCategoria(tipi).map(([cat, catItems]) => (
                                    <optgroup key={cat} label={cat}>
                                        {catItems.map(t => (
                                            <option key={t.id_tipo_nom} value={t.id_tipo_nom}>
                                                {t.denominazione}{Number(t.obbligatorio) === 1 ? " (obbligatoria)" : ""}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </Form.Select>
                            {tipoHelp && <Form.Text className="text-muted d-block">{tipoHelp}</Form.Text>}
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Titolo (se altro)</Form.Label>
                            <Form.Control size="sm" value={form.titolo} onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Tipo incarico</Form.Label>
                            <Form.Select size="sm" value={form.tipo_soggetto} onChange={e => setForm(f => ({
                                ...f,
                                tipo_soggetto: e.target.value,
                                id_lavoratore: "",
                                nominativo: "",
                                ente_esterno: "",
                            }))}>
                                <option value="LAVORATORE">Interno (lavoratore)</option>
                                <option value="ESTERNO">Esterno (consulente/studio)</option>
                            </Form.Select>
                        </Col>
                        {form.tipo_soggetto === "LAVORATORE" ? (
                            <Col xs={12} md={8}>
                                <Form.Label className="small mb-0">Lavoratore incaricato</Form.Label>
                                <LavoratoreSearchPicker
                                    idAzienda={idAzienda}
                                    value={form.id_lavoratore}
                                    onChange={v => setForm(f => ({ ...f, id_lavoratore: v }))}
                                    placeholder="Cerca cognome, nome, CF…"
                                />
                            </Col>
                        ) : (
                            <>
                                <Col xs={12} md={4}>
                                    <Form.Label className="small mb-0">Nominativo</Form.Label>
                                    <Form.Control size="sm" value={form.nominativo} onChange={e => setForm(f => ({ ...f, nominativo: e.target.value }))} />
                                </Col>
                                <Col xs={12} md={4}>
                                    <Form.Label className="small mb-0">Studio / società esterna</Form.Label>
                                    <Form.Control size="sm" value={form.ente_esterno} onChange={e => setForm(f => ({ ...f, ente_esterno: e.target.value }))} placeholder="Es. Consulente RSPP S.r.l." />
                                </Col>
                            </>
                        )}
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Data nomina</Form.Label>
                            <Form.Control type="date" size="sm" value={form.data_nomina} onChange={e => setForm(f => ({ ...f, data_nomina: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Scadenza aggiornamento / fine incarico</Form.Label>
                            <Form.Control type="date" size="sm" value={form.data_scadenza} onChange={e => setForm(f => ({ ...f, data_scadenza: e.target.value }))} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Periodicità aggiornamento (mesi)</Form.Label>
                            <Form.Control type="number" size="sm" min="0" value={form.periodicita_mesi} onChange={e => setForm(f => ({ ...f, periodicita_mesi: e.target.value }))} placeholder={tipoSel?.periodicita_mesi || ""} />
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small mb-0">Note</Form.Label>
                            <Form.Control as="textarea" rows={2} size="sm" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
                        </Col>
                        {sedi.length > 0 && (
                            <Col xs={12}>
                                <Form.Label className="small mb-1 d-block">
                                    Sedi di appartenenza
                                    <span className="text-muted"> (es. medico competente per più sedi)</span>
                                </Form.Label>
                                <div className="border rounded p-2" style={{ background: "#ffffff" }}>
                                    <Row className="g-1">
                                        {sedi.map(s => (
                                            <Col xs={12} md={6} key={s.id_sede}>
                                                <Form.Check
                                                    type="checkbox"
                                                    id={`nomina-sede-${s.id_sede}`}
                                                    label={s.denominazione}
                                                    checked={selectedSedi.includes(Number(s.id_sede))}
                                                    onChange={() => toggleSede(s.id_sede)}
                                                />
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            </Col>
                        )}
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Allegato decreto nomina (PDF)</Form.Label>
                            <Form.Control type="file" size="sm" accept=".pdf,.PDF" onChange={e => setFile(e.target.files?.[0] || null)} />
                        </Col>
                    </Row>
                    <div className="mt-2 d-flex gap-2">
                        <Button size="sm" variant="primary" onClick={submit} disabled={saving}>
                            {saving ? <Spinner size="sm" animation="border" /> : "Salva nomina"}
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => setShow(false)} disabled={saving}>Annulla</Button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-muted small"><Spinner size="sm" animation="border" className="me-2" />Caricamento…</div>
            ) : items.length === 0 ? (
                <div className="text-muted small">
                    Nessuna figura registrata. Indica almeno RSPP e medico competente; aggiungi RLS, preposti e addetti emergenze secondo organizzazione aziendale.
                </div>
            ) : (
                <Table size="sm" bordered hover responsive className="mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Figura</th>
                            <th>Incaricato</th>
                            <th>Nomina</th>
                            <th>Aggiornamento</th>
                            <th>Stato</th>
                            <th className="text-end">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(n => (
                            <tr key={n.id_nomina}>
                                <td>
                                    <div>{n.titolo || n.tipo_denominazione || "—"}</div>
                                    <div className="text-muted small">
                                        {n.tipo_categoria || ""}
                                        {n.tipo_soggetto === "ESTERNO" ? " · Esterno" : " · Interno"}
                                        {n.ente_esterno ? ` · ${n.ente_esterno}` : ""}
                                    </div>
                                </td>
                                <td>
                                    {displayIncaricato(n)}
                                    {n.sedi_denominazioni && (
                                        <div className="text-muted small">
                                            <i className="bi bi-geo-alt me-1" />{n.sedi_denominazioni}
                                        </div>
                                    )}
                                </td>
                                <td>{fmtDate(n.data_nomina)}</td>
                                <td>{fmtDate(n.data_scadenza)}</td>
                                <td>{statoBadge(n.stato, n.giorni_alla_scadenza)}</td>
                                <td className="text-end">
                                    {n.path_file && (
                                        <Button size="sm" variant="danger" className="me-1" onClick={() => onDownload(n)}>
                                            <i className="bi bi-file-earmark-pdf me-1" />PDF
                                        </Button>
                                    )}
                                    <Button size="sm" variant="outline-danger" onClick={() => onDelete(n)}>
                                        <i className="bi bi-trash" />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    );
}
